/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2023, Zumper
 * @description ListingCard
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { selectFilters } from '@zcode-ui/filters'
import { useRouteProps } from '@zcode-ui/hook'
import {
  ListingCardSizes,
  type NzrListingCardProps,
  ListingCard as UIListingCard,
} from '@zcode-ui/web-zumper-features'
import { selectIsBot, selectNow } from '@zcode-ui/web-zumper-global-state'
import { AnalyticsC } from '@zcode/nr-utils'
import { type Ref, forwardRef, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { selectFiltersPath, selectIsHFR } from '../../../../../modules/router/selectors'
import { ListCardBadges } from '../../../../ListCardBadge/ListCardBadges'
import { DetailPageLink } from '../../../../detail/DetailPageLink/DetailPageLink'
import { ListCardRating } from '../../../../detail/RatingsAndReviews/ListCardRating'
import { FavoriteContainer } from '../../../../favorite/FavoriteContainer'
import { selectShouldLimitLeads } from '../../../../message/MessageForm/helpers/getShouldLimitLeads'
import { CarouselWrapper } from '../STRListItem/CarouselWrapper'
import { ListingCardMessageButton } from './ListingCardContactSection/ListingCardMessageButton'
import { ListingCardTourButton } from './ListingCardContactSection/ListingCardTourButton'
import { createGetHoodPropertyText } from './helpers/createGetHoodPropertyText'
import { updateAmenities } from './helpers/updateAmenities'
import { useGetCommonEntityData } from './helpers/useGetCommonEntityData'
import { useOnPaidPhoneClick } from './helpers/useOnPaidPhoneClick'

export type {
  NzrListingCardSmProps as ListingCardSmProps,
  NzrListingCardMdLgProps as ListingCardMdLgProps,
} from '@zcode-ui/web-zumper-features'
export type ListingCardProps = NzrListingCardProps

export const ListingCard = forwardRef((props: NzrListingCardProps, ref: Ref<HTMLDivElement>) => {
  const routeProps = useRouteProps()
  const filtersPath = selectFiltersPath(routeProps)
  const filters = useSelector(selectFilters)
  const isHFR = selectIsHFR(routeProps)
  const updatedAmenities = useMemo(
    () =>
      updateAmenities({
        filters,
        filtersPath,
        entity: props.entity,
        isHFR,
        limit: 3,
      }),
    [filters, filtersPath, props.entity, isHFR]
  )

  const isBot = useSelector(selectIsBot)
  const now = useSelector(selectNow)
  const shouldLimitLeads = useSelector(selectShouldLimitLeads)
  const getCommonEntityData = useGetCommonEntityData()
  const onPaidPhoneClick = useOnPaidPhoneClick()
  const location = useLocation()
  const getHoodPropertyText = useMemo(() => createGetHoodPropertyText(location), [location])

  return props.size === ListingCardSizes.SM ? (
    <UIListingCard
      Carousel={CarouselWrapper}
      ListCardRating={ListCardRating}
      getCommonEntityData={getCommonEntityData}
      getHoodPropertyText={getHoodPropertyText}
      ref={ref}
      {...props}
    />
  ) : typeof props.isChecked === 'boolean' || props.hideCTAs ? (
    <UIListingCard
      Carousel={CarouselWrapper}
      DetailPageLink={DetailPageLink}
      FavoriteContainer={FavoriteContainer}
      ListCardRating={ListCardRating}
      ListCardBadges={ListCardBadges}
      getCommonEntityData={getCommonEntityData}
      getHoodPropertyText={getHoodPropertyText}
      isBot={isBot}
      now={now}
      ref={ref}
      updatedAmenities={updatedAmenities}
      {...props}
      parentName={props.parentName || AnalyticsC.LIST_ITEM}
    />
  ) : (
    <UIListingCard
      Carousel={CarouselWrapper}
      DetailPageLink={DetailPageLink}
      FavoriteContainer={FavoriteContainer}
      ListCardRating={ListCardRating}
      ListingCardMessageButton={ListingCardMessageButton}
      ListingCardTourButton={ListingCardTourButton}
      ListCardBadges={ListCardBadges}
      getCommonEntityData={getCommonEntityData}
      getHoodPropertyText={getHoodPropertyText}
      isBot={isBot}
      now={now}
      onPaidPhoneClick={onPaidPhoneClick}
      ref={ref}
      shouldLimitLeads={shouldLimitLeads}
      updatedAmenities={updatedAmenities}
      {...props}
      parentName={props.parentName || AnalyticsC.LIST_ITEM}
    />
  )
})
